/* eslint-disable max-lines */
import { createSelector } from 'reselect'
import { ConfigManager } from '~/managers/ConfigManager'
import {
  selectIsFiatWalletOnly,
  selectUserBizId,
  selectUserEmail,
  selectUserIdentifier,
} from '~/store/route/selectors'
import { CommonState } from '~/store/types'
import { FLIPPER_KEYS } from './const'
import { Flipper, FLIPPER_STATUS } from './types'

const isFlipperEnabled = (flippers: Flipper[], featureKey: string, flipperId: string) => {
  return !!flippers.find(
    (f) =>
      f.featureKey === featureKey &&
      f.flipperId === flipperId &&
      f.status === FLIPPER_STATUS.SUCCEEDED &&
      f.enabled === true
  )
}

const isFlipperWithoutId = (flippers: Flipper[], featureKey: string) => {
  return !!flippers.find(
    (f) =>
      f.featureKey === featureKey && f.status === FLIPPER_STATUS.SUCCEEDED && f.enabled === true
  )
}

// TODO(benedictkhoo): deduplicate Redux root state
export const selectFlippers = (state: CommonState) => state.flippers.flippers

export const selectFlipper = (featureKey: string) =>
  createSelector([selectFlippers], (flippers: Flipper[]) =>
    flippers.find((i) => i.featureKey === featureKey && !i.flipperId)
  )

export const selectFlipperWithId = (featureKey: string, flipperId: string) =>
  createSelector([selectFlippers], (flippers: Flipper[]) =>
    flippers.find((i) => i.featureKey === featureKey && i.flipperId === flipperId)
  )

// TODO(benedictkhoo): remove after assets unification is done
export const selectIsAssetUnificationPhase1Ready = createSelector(
  [selectFlippers, selectIsFiatWalletOnly, selectUserEmail],
  (flippers, isFiatWalletOnly, email) => {
    const isAppSite = ConfigManager.isAppSite()
    const featureKey =
      isAppSite || isFiatWalletOnly
        ? FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_1_STAGE_1 // Personal & Fiat Biz users
        : FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_1_STAGE_2 // Default Biz users
    const flipper = flippers.find((f) => f.featureKey === featureKey && f.flipperId === email)

    return flipper?.status === FLIPPER_STATUS.SUCCEEDED
  }
)

// TODO(benedictkhoo): remove after assets unification is done
export const selectIsWhitelistedForAssetUnificationPhase1 = createSelector(
  [selectFlippers, selectUserBizId],
  (flippers, flipperId) => {
    if (ConfigManager.isAppSite()) {
      return true
    }

    const featureKey = FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_1_BLACKLIST
    const flipper = flippers.find((f) => f.featureKey === featureKey && f.flipperId === flipperId)

    if (flipper) {
      return flipper.status === FLIPPER_STATUS.SUCCEEDED && flipper.enabled === false
    }

    return true
  }
)

const selectIsBlacklistFlipperForAssetUnificationPhase2Ready = createSelector(
  [selectFlippers],
  (flippers) => {
    const featureKey = FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_2_BLACKLIST
    const flipper = flippers.find((f) => f.featureKey === featureKey)

    return flipper?.status === FLIPPER_STATUS.SUCCEEDED
  }
)

const selectIsPhase2FlipperForAssetUnificationPhase2Ready = createSelector(
  [selectFlippers],
  (flippers) => {
    const featureKey = FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_2
    const flipper = flippers.find((f) => f.featureKey === featureKey)

    return flipper?.status === FLIPPER_STATUS.SUCCEEDED
  }
)

const selectIsBlacklistedForAssetUnificationPhase2 = createSelector(
  [selectFlippers, selectUserIdentifier],
  (flippers, id) => {
    const featureKey = FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_2_BLACKLIST

    return isFlipperEnabled(flippers, featureKey, id)
  }
)

const selectIsAssetUnificationPhase2FlipperEnabled = createSelector(
  [selectFlippers, selectUserIdentifier],
  (flippers, id) => {
    const featureKey = FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_2
    return isFlipperEnabled(flippers, featureKey, id)
  }
)

// TODO(benedictkhoo): remove after assets unification is done
export const selectIsAssetUnificationPhase1Enabled = createSelector(
  [
    selectFlippers,
    selectIsFiatWalletOnly,
    selectUserEmail,
    selectIsWhitelistedForAssetUnificationPhase1,
    selectIsBlacklistedForAssetUnificationPhase2,
  ],
  (flippers, isFiatWalletOnly, email, isWhitelisted, isBlacklistedPhase2) => {
    const isAppSite = ConfigManager.isAppSite()

    if (!isWhitelisted || isBlacklistedPhase2) {
      return false
    }

    const featureKey =
      isAppSite || isFiatWalletOnly
        ? FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_1_STAGE_1 // Personal & Fiat Biz users
        : FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_1_STAGE_2 // Default Biz users

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

// TODO(benedictkhoo): remove after assets unification is done
export const selectIsAssetUnificationPhase2Ready = createSelector(
  [
    selectIsPhase2FlipperForAssetUnificationPhase2Ready,
    selectIsBlacklistFlipperForAssetUnificationPhase2Ready,
  ],
  (isPhase2FlipperReady, isBlacklistFlipperReady) => {
    return isBlacklistFlipperReady && isPhase2FlipperReady
  }
)

// TODO(benedictkhoo): remove after assets unification is done
export const selectIsAssetUnificationPhase2Enabled = createSelector(
  [selectIsAssetUnificationPhase2FlipperEnabled, selectIsBlacklistedForAssetUnificationPhase2],
  (isPhase2Enabled, isBlacklisted) => {
    return isPhase2Enabled && !isBlacklisted
  }
)

export const selectIsOtcTrxHistoryEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const isAppSite = ConfigManager.isAppSite()
    const featureKey = isAppSite
      ? FLIPPER_KEYS.SG_OTC_TRX_HISTORY_APP_DASHBOARD
      : FLIPPER_KEYS.SG_OTC_TRX_HISTORY_BIZ_DASHBOARD

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsUsdtUIEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const isAppSite = ConfigManager.isAppSite()
    const featureKey = isAppSite ? FLIPPER_KEYS.APP_DASHBOARD_USDT : FLIPPER_KEYS.BIZ_DASHBOARD_USDT

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsXusdUIEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.XUSD_DASHBOARD_ENABLE

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsUsdTransferInUIEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.USD_TRANSFER_IN

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsUsdTransferOutUIEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.USD_TRANSFER_OUT

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsCoolingDownEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.COOLING_DOWN

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsTransferInUIRevampEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.TRANSFER_IN_UI_REVAMP

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsTypeformSurveyVisible = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.STRAITSX_TYPEFORM_SURVEY

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsDpt2TncEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.DPT2_TNC

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsSgdMepsDepositEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.SGD_MEPS_DEPOSIT

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsSgdMepsWithdrawEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.SGD_MEPS_WITHDRAW

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsSgdSwiftDepositEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.SGD_SWIFT_DEPOSIT

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsSgdSwiftWithdrawEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.SGD_SWIFT_WITHDRAW

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsUsdcErc20DepositEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.USDC_ERC20_DEPOSIT

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsUsdcErc20WithdrawEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.USDC_ERC20_WITHDRAWAL

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsUsdcMaticDepositEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.USDC_MATIC_DEPOSIT

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsUsdcMaticWithdrawEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.USDC_MATIC_WITHDRAWAL

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsUsdtBep20WithdrawEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.USDT_BEP20_WITHDRAWAL

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsUsdtErc20DepositEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.USDT_ERC20_DEPOSIT

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsXusdcErc20DepositEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.XUSD_ERC20_DEPOSIT

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsXusdcErc20WithdrawEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.XUSD_ERC20_WITHDRAWAL

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsUsdtErc20WithdrawEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.USDT_ERC20_WITHDRAWAL

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsSwapEnabled = createSelector(
  [selectFlippers, selectUserEmail, selectIsFiatWalletOnly],
  (flippers, email, isFiatWalletOnly) => {
    if (isFiatWalletOnly) {
      return false
    }

    const featureKey = ConfigManager.isAppSite()
      ? FLIPPER_KEYS.APP_DASHBOARD_SWAP
      : FLIPPER_KEYS.BIZ_DASHBOARD_SWAP

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

// Developer Tools Self Serve
export const selectIsDashboardSelfServePhase1Enabled = createSelector(
  [selectFlippers],
  (flippers) => {
    const featureKey = FLIPPER_KEYS.DASHBOARD_SELF_SERVE_PHASE_1
    return isFlipperWithoutId(flippers, featureKey)
  }
)

export const selectIsDashboardSelfServeIpWhitelistingEnabled = createSelector(
  [selectFlippers],
  (flippers) => {
    const featureKey = FLIPPER_KEYS.DASHBOARD_SELF_SERVE_IP_WHITELISTING

    return isFlipperWithoutId(flippers, featureKey)
  }
)

export const selectIsDashboardSelfServeSandboxToolsEnabled = createSelector(
  [selectFlippers],
  (flippers) => {
    const featureKey = FLIPPER_KEYS.DASHBOARD_SELF_SERVE_SANDBOX_TOOLS

    return isFlipperWithoutId(flippers, featureKey)
  }
)

export const selectIsBizEarnEnabled = createSelector(
  [selectFlippers, selectUserBizId],
  (flippers, bizId) => {
    const featureKey = FLIPPER_KEYS.STRAITSX_BIZ_DASHBOARD_USE_EARN

    return isFlipperEnabled(flippers, featureKey, bizId || '')
  }
)

export const selectIsBizEarnUSDCEnabled = createSelector(
  [selectFlippers, selectUserBizId],
  (flippers, bizId) => {
    const featureKey = FLIPPER_KEYS.STRAITSX_BIZ_DASHBOARD_USE_EARN_USDC

    return isFlipperEnabled(flippers, featureKey, bizId || '')
  }
)

export const selectIsBizEarnXUSDEnabled = createSelector(
  [selectFlippers, selectUserBizId],
  (flippers, bizId) => {
    const featureKey = FLIPPER_KEYS.STRAITSX_BIZ_DASHBOARD_USE_EARN_XUSD

    return isFlipperEnabled(flippers, featureKey, bizId || '')
  }
)

export const selectIsSwapAiRateEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.SWAP_AI_RATE

    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsSwapUseQuoteExpiresInEnabled = createSelector(
  [selectFlipper(FLIPPER_KEYS.SWAP_USE_QUOTE_EXPIRES_IN)],
  (flipper) => {
    return flipper?.status === FLIPPER_STATUS.SUCCEEDED && flipper?.enabled === true
  }
)

export const selectIsZilliqaDepositSupportEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.XSGD_ZRC2_DEPOSIT_SUPPORT
    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsDirectMintBizPhase1Enabled = createSelector(
  [selectFlippers, selectUserBizId],
  (flippers, bizId) => {
    if (ConfigManager.isAppSite()) return false
    const featureKey = FLIPPER_KEYS.STRAITSX_DASHBOARD_DIRECT_MINT_BIZ_PHASE_1
    return isFlipperEnabled(flippers, featureKey, bizId || '')
  }
)
// Signing Key Settings Menu
export const selectIsDashboardSigningKeyMenuEnabled = createSelector(
  [selectFlippers],
  (flippers) => {
    const featureKey = FLIPPER_KEYS.DASHBOARD_SIGNING_KEY_MENU
    return isFlipperWithoutId(flippers, featureKey)
  }
)

export const selectIsDashboardPublicPrivateKeyMenuEnabled = createSelector(
  [selectFlippers],
  (flippers) => {
    const featureKey = FLIPPER_KEYS.DASHBOARD_PUBLIC_PRIVATE_KEY_MENU
    return isFlipperWithoutId(flippers, featureKey)
  }
)

export const selectIsWhitelistModalV2Enabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.DASHBOARD_WHITELIST_MODAL_V2
    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsBankAccountTfOutActionButtonEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.BANK_ACCOUNT_TF_OUT_ACTION_BUTTON
    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsAsbSandboxEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    if (ConfigManager.isAppSite()) return false
    const featureKey = FLIPPER_KEYS.ASB_SANDBOX_UI
    return isFlipperEnabled(flippers, featureKey, email)
  }
)

export const selectIsRestrictedCountryFlipperEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.BLOCK_USER_RESIDES_IN_RESTRICTED_COUNTRY
    return isFlipperEnabled(flippers, featureKey, email || '')
  }
)

export const selectIsInfiniteScrollTransactionsEnabled = createSelector(
  [selectFlippers, selectUserEmail],
  (flippers, email) => {
    const featureKey = FLIPPER_KEYS.INFINITE_SCROLL_TRANSACTIONS
    return isFlipperEnabled(flippers, featureKey, email)
  }
)
